import { ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { CallerName } from '@/components/crm/CallerName';
import type { CallFollowUp, ClassifiedCall, RouteStep } from '@/lib/reports/callClassification';

const clock = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' });
const dayClock = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

function followUpText(followUp: CallFollowUp) {
  const at = clock.format(new Date(followUp.atMs));
  return followUp.kind === 'calledBack'
    ? `Called back ${at} by ${followUp.by}${followUp.answered ? '' : ' (no answer)'}`
    : `Rang again ${at}, answered by ${followUp.by}`;
}

function Route({ steps }: { steps: RouteStep[] }) {
  return (
    <ol className="flex flex-wrap items-center gap-x-1 gap-y-0.5 text-xs text-gray-600 dark:text-gray-300">
      {steps.map((step, index) => (
        <li key={index} className="inline-flex items-center gap-1">
          {index > 0 && (
            <span aria-hidden="true" className="text-gray-400">
              →
            </span>
          )}
          <span className={step.kind === 'voicemail' ? 'font-medium text-amber-700 dark:text-amber-300' : ''}>
            {step.label}
            {step.seconds !== undefined && <span className="text-gray-400 dark:text-gray-500"> ({step.seconds}s)</span>}
          </span>
        </li>
      ))}
    </ol>
  );
}

/** One row per call where the caller never spoke to anyone: missed, or left in voicemail. */
export function MissedCallsList({
  calls,
  showDate,
  onSelect,
}: {
  calls: ClassifiedCall[];
  /** For ranges longer than a day. */
  showDate: boolean;
  onSelect: (call: ClassifiedCall) => void;
}) {
  if (calls.length === 0) {
    return (
      <p className="rounded-2xl border border-dashed border-gray-300 dark:border-gray-600 px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
        No missed calls: every caller who reached a queue or extension spoke to someone.
      </p>
    );
  }
  const time = showDate ? dayClock : clock;
  return (
    <div className="overflow-x-auto rounded-2xl border border-gray-200 dark:border-gray-700">
      <table className="w-full text-sm text-gray-900 dark:text-gray-100">
        <thead className="bg-panel dark:bg-gray-700 text-left text-gray-700 dark:text-gray-200">
          <tr>
            <th className="px-4 py-2.5 font-semibold">Time</th>
            <th className="px-4 py-2.5 font-semibold">Caller</th>
            <th className="px-4 py-2.5 text-right font-semibold" title="Total time it rang at queues and extensions">
              Wait
            </th>
            <th className="px-4 py-2.5 font-semibold">Route</th>
            <th className="px-4 py-2.5 font-semibold">Result</th>
            <th className="w-8 px-2 py-2.5">
              <span className="sr-only">Details</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800/70">
          {calls.map((call) => (
            <tr
              key={call.uid}
              onClick={(event) => {
                // A contact name is its own button
                if (!(event.target as HTMLElement).closest('button')) onSelect(call);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelect(call);
                }
              }}
              tabIndex={0}
              title="Show call details"
              className="cursor-pointer align-top hover:bg-gray-50 focus:bg-gray-50 focus:outline-none dark:hover:bg-gray-700/60 dark:focus:bg-gray-700/60"
            >
              <td className="whitespace-nowrap px-4 py-2.5 tabular-nums text-gray-600 dark:text-gray-300">
                {call.atMs ? time.format(new Date(call.atMs)) : call.time}
              </td>
              <td className="px-4 py-2.5">
                <CallerName value={call.caller} compact />
              </td>
              <td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums">{call.waitSeconds}s</td>
              <td className="min-w-[220px] px-4 py-2.5">
                <Route steps={call.route} />
              </td>
              <td className="px-4 py-2.5">
                <div className="flex flex-col items-start gap-1">
                  {call.outcome === 'voicemail' ? (
                    <Badge variant="warning">Voicemail</Badge>
                  ) : (
                    <Badge variant="danger">Missed</Badge>
                  )}
                  {call.followUp && (
                    <>
                      <Badge variant="success" className="whitespace-nowrap">
                        Called back
                      </Badge>
                      <span className="text-xs text-gray-500 dark:text-gray-400">{followUpText(call.followUp)}</span>
                    </>
                  )}
                </div>
              </td>
              <td className="px-2 py-2.5 text-gray-400">
                <ChevronRight size={16} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
